import _ from 'lodash';
import {
  GET_USER_BY_OKTA_USER_ID,
  AUTH_ERROR,
  DEAUTH_USER,
  RESET_PASSWORD,
  CHANGE_PASSWORD,
  PUT_USER_AUTOASSIGN,
  DELETE_USERS_AUTOREPLIES,
  PUT_USERS_AUTOREPLIES,
  POST_USERS_AUTOREPLIES,
  GET_USERS_AUTOREPLIES,
  POST_OPERATORS_AVAILABILITY,
  AUTH_USER,
  SET_ACCESS_TOKEN_ERROR_MESSAGE,
  SET_CURRENT_USER_ROLES,
  SET_CURRENT_USER_GROUPS,
  UPDATE_PROFILE,
} from '../constants/actions';
import { formatInit, formatSuccess, formatError } from '../constants/endpoints';
import { SUCCESS, TIMEOUT_DURATION } from '../constants/general';

import * as pendoHelpers from '../helpers/pendo/pendo';

import {
  CHAT_ALL_INBOX,
  CASES_TAB,
  CHATS_TAB,
  CLAIMS_TAB,
  POLICY_TAB,
  MASSNOTIFICATIONS_TAB,
  USER_MANAGEMENT_TAB,
  ANALYTICS_TAB,
  RESTRICT_SEND_MESSAGE,
  RESTRICT_CASE_ACTIONS,
  RESTRICT_CASE_EDITING,
  RESTRICT_CASE_ASSIGNMENT,
  RESTRICT_CHAT_PARTICIPANT_CONFIGURATION,
  RESTRICT_NOTES_EDITING,
  RESTRICT_SURVEY_UI,
  RESTRICT_TRANSLATE_EDITING,
  WEBAPP_CASE_CREATION,
  MULTI_PARTY_TEXTING,
} from '../constants/permissions';
import { FOCUSED_OPERATOR, VIEW_ONLY_OPERATOR } from '../constants/roles';
import { updateAllAutoReplies } from '@app/helpers/auto-replies';
import { setupDatadogRumUserAndContext } from '@app/helpers/datadog-rum';

export const initialState = {
  error: false,
  user: {},
  inactivityTimeout: TIMEOUT_DURATION,
  permissions: [],
  retrievedUserInfo: null,
  passwordIsChanged: false,
  passwordResetted: false,
  pendingRequest: false,
  oktaUserId: null,
  logout: false,
  score: '',
  userAutoReply: {
    generalAutoReplies: {
      list: [],
    },
    oooReplies: {
      list: [],
    },
  },
  userStatus: false,
  accessTokenErrorMessage: null,
};

const standardPermissions = [
  CHAT_ALL_INBOX, // Ability to view chat all inbox
];

const personas = {};
// user roles are not separated by _ characters
_.set(personas, FOCUSED_OPERATOR, {
  EXCLUDES: [
    CHAT_ALL_INBOX,
    CASES_TAB,
    CLAIMS_TAB,
    POLICY_TAB,
    MASSNOTIFICATIONS_TAB,
    USER_MANAGEMENT_TAB,
    ANALYTICS_TAB,
  ],
});
_.set(personas, VIEW_ONLY_OPERATOR, {
  INCLUDES: [
    CASES_TAB,
    CHATS_TAB,
    CLAIMS_TAB,
    POLICY_TAB,
    RESTRICT_SEND_MESSAGE,
    RESTRICT_CASE_ACTIONS,
    RESTRICT_CASE_EDITING,
    RESTRICT_CASE_ASSIGNMENT,
    RESTRICT_CHAT_PARTICIPANT_CONFIGURATION,
    RESTRICT_SURVEY_UI,
    RESTRICT_NOTES_EDITING,
    RESTRICT_TRANSLATE_EDITING,
  ],
  EXCLUDES: [
    WEBAPP_CASE_CREATION,
    MULTI_PARTY_TEXTING,
    MASSNOTIFICATIONS_TAB,
    ANALYTICS_TAB,
  ],
});

const availablePersonas = Object.keys(personas);

const applyPersonas = (permissions, roles) => {
  let updated = permissions;
  _.intersection(roles, availablePersonas).forEach((r) => {
    const { INCLUDES = [], EXCLUDES = [] } = personas[r];
    updated = _.union(updated, INCLUDES);
    updated = _.difference(updated, EXCLUDES);
  });
  return updated;
};

const isLoggedInUserAutoReply = (autoReply, user) => {
  const requestedId = autoReply?.meta?.entityIds?.[0];
  if (requestedId !== undefined && requestedId !== null) {
    return (
      requestedId === user?._id ||
      requestedId === user?.id ||
      String(requestedId) === String(user?._id) ||
      String(requestedId) === String(user?.id)
    );
  }
  return (
    (autoReply?.payload?.pk && autoReply?.payload?.pk?.includes(user?._id)) ||
    (autoReply?.payload[0]?.pk &&
      autoReply?.payload[0]?.pk?.includes(user?._id))
  );
};

const auth = (state = initialState, action) => {
  switch (action.type) {
    case GET_USER_BY_OKTA_USER_ID: {
      const user = action.payload;
      let permissions = _.union(user.permissions, standardPermissions);

      const { roles } = user;
      permissions = applyPersonas(permissions, roles);

      const userClone = _.cloneDeep(user);
      userClone.ownerOrganization = userClone.organization;
      delete userClone.permissions;
      delete userClone.organization;

      return {
        ...state,
        user: userClone,
        permissions,
        retrievedUserInfo: 'loading',
      };
    }
    case formatSuccess(GET_USER_BY_OKTA_USER_ID): {
      return { ...state, retrievedUserInfo: SUCCESS };
    }
    case formatError(GET_USER_BY_OKTA_USER_ID): {
      const error = action.payload?.error?.name;
      const errorCode = action.payload?.error?.status;
      if (error === 'failedToGetUser' && errorCode === 500) {
        return {
          ...state,
          logout: true,
          retrievedUserInfo: 'user-lookup-failure',
        };
      }
      return {
        ...state,
        logout: true,
        retrievedUserInfo: 'health-check-failure',
      };
    }
    case formatInit(RESET_PASSWORD): {
      return { ...state, pendingRequest: true };
    }
    case formatSuccess(RESET_PASSWORD): {
      return {
        ...state,
        passwordResetted: true,
        error: false,
        pendingRequest: false,
      };
    }
    case formatSuccess(CHANGE_PASSWORD): {
      return {
        ...state,
        passwordIsChanged: true,
        error: false,
        pendingRequest: false,
      };
    }
    case AUTH_ERROR: {
      const error =
        action.payload && typeof action.payload === 'object'
          ? 'Auth error'
          : action.payload;
      return {
        ...state,
        error,
        pendingRequest: false,
        passwordIsChanged: false,
      };
    }
    case AUTH_USER: {
      const { user } = action.payload;
      // Transform organization to ownerOrganization for consistency
      const userForDatadog = user?.organization
        ? { ...user, ownerOrganization: user.organization }
        : user;
      setupDatadogRumUserAndContext(userForDatadog);
      return { ...state };
    }
    case SET_CURRENT_USER_ROLES: {
      const { roles } = action.payload;
      const permissions = applyPersonas(state.permissions, roles);
      return { ...state, user: { ...state.user, roles }, permissions };
    }
    case SET_CURRENT_USER_GROUPS: {
      const updatedState = {
        ...state,
        user: {
          ...state.user,
          groups: action.payload.groups,
          groupLeads: action.payload.groupLeads,
        },
      };
      if (process?.env?.ENABLE_THIRD_PARTY_SCRIPTS) {
        pendoHelpers.identify(updatedState?.user);
      }
      return updatedState;
    }
    case DEAUTH_USER: {
      return { ...state, logout: true };
    }
    case PUT_USER_AUTOASSIGN: {
      const isAutoAssign = _.get(
        action,
        'payload.isAutoAssign',
        state.user.isAutoAssign,
      );
      return {
        ...state,
        user: {
          ...state.user,
          isAutoAssign,
        },
      };
    }
    case DELETE_USERS_AUTOREPLIES: {
      if (!isLoggedInUserAutoReply(action, state?.user)) {
        return state;
      }
      return updateAllAutoReplies(state, action);
    }
    case PUT_USERS_AUTOREPLIES: {
      if (!isLoggedInUserAutoReply(action, state?.user)) {
        return state;
      }
      return updateAllAutoReplies(state, action);
    }
    case POST_USERS_AUTOREPLIES: {
      if (!isLoggedInUserAutoReply(action, state?.user)) {
        return state;
      }
      return updateAllAutoReplies(state, action);
    }
    case GET_USERS_AUTOREPLIES: {
      if (!isLoggedInUserAutoReply(action, state?.user)) {
        return state;
      }
      return updateAllAutoReplies(state, action);
    }
    case POST_OPERATORS_AVAILABILITY: {
      if (!isLoggedInUserAutoReply(action, state?.user)) {
        return state;
      }
      return {
        ...state,
        userStatus:
          action.payload.availabilityState === 'AWAY' ? 'away' : 'active',
      };
    }
    case SET_ACCESS_TOKEN_ERROR_MESSAGE: {
      return {
        ...state,
        accessTokenErrorMessage: action.payload,
      };
    }
    case UPDATE_PROFILE: {
      const updatedUser = action.payload;
      if (
        (updatedUser?._id || updatedUser?.id) ===
        (state?.user?._id || state?.user?.id)
      ) {
        return {
          ...state,
          user: { ...state.user, ...updatedUser },
        };
      }
      return state;
    }
    default: {
      return state;
    }
  }
};

export default auth;
